import React from 'react';
import { Paper, Box, Typography, Button, Divider } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IRadius, INumericSx } from '../insightsTheme';

/**
 * UpcomingCmAgenda — next-7-days CM schedule with a concise narrative and a preview of
 * the first 1–3 experiments that will start in that window (so the card shows what's
 * behind the number, not just the number).
 *
 * Props:
 *   data: { messageType, timePeriodType, pendingCount, acceptedCount, totalCount,
 *           nextExperimentDate, daysUntilNext, ... } from /experiments/upcoming-message
 *   upcoming: Array<{ id, productName, machineId, dateFrom, status }> — experiments to render
 *   onSeeMore / onProseClick / onCountdownClick / onExperimentClick
 */
const UpcomingCmAgenda = ({
  data,
  upcoming = [],
  width = 300,
  onSeeMore,
  onProseClick,
  onCountdownClick,
  onExperimentClick,
}) => {
  const { t } = useTranslation();

  const whenLabel = {
    this_week: t('insights_v2_agenda_this_week'),
    next_2_weeks: t('insights_v2_agenda_next_2_weeks'),
    next_weeks: t('insights_v2_agenda_next_2_weeks'),
    next_month: t('insights_v2_agenda_next_month'),
    next_months: t('insights_v2_agenda_next_months'),
  }[data?.timePeriodType] || t('insights_v2_agenda_this_week');

  const mt = data?.messageType;
  let line;
  if (!data || mt === 'no_experiments') {
    line = t('insights_v2_agenda_none');
  } else if (mt === 'only_pending') {
    line = t('insights_v2_agenda_pending_only', { count: data.pendingCount });
  } else if (mt === 'only_accepted') {
    line = t('insights_v2_agenda_accepted_only', { count: data.acceptedCount, when: whenLabel });
  } else {
    line = t('insights_v2_agenda_both', {
      pending: data.pendingCount,
      accepted: data.acceptedCount,
      when: whenLabel,
    });
  }

  const daysUntil = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const statusDot = (status) =>
    ({
      pending: IC.amb,
      accepted: IC.grn,
      running: IC.grn,
    }[status] || IC.txM);

  const preview = upcoming.slice(0, 3);

  return (
    <Paper
      variant="outlined"
      sx={{
        width,
        flexShrink: 0,
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <EventOutlinedIcon sx={{ fontSize: 16, color: IC.blu }} />
        <Typography sx={{ fontSize: `${IFontSizes.md}px`, fontWeight: IFontWeights.strong, color: IC.tx }}>
          {t('insights_v2_agenda_title')}
        </Typography>
      </Box>

      <Typography
        role={onProseClick ? 'button' : undefined}
        tabIndex={onProseClick ? 0 : undefined}
        onClick={onProseClick || undefined}
        onKeyDown={(e) => {
          if (onProseClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onProseClick();
          }
        }}
        aria-label={onProseClick ? t('insights_v2_a11y_open_agenda') : undefined}
        sx={{
          fontSize: `${IFontSizes.sm}px`,
          color: IC.tx2,
          lineHeight: 1.5,
          cursor: onProseClick ? 'pointer' : 'default',
          transition: 'color 0.15s ease',
          '&:hover': onProseClick ? { color: IC.tx } : undefined,
        }}
      >
        {line}
      </Typography>

      {preview.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mt: 0.25,
          }}
        >
          {preview.map((exp) => {
            const days = daysUntil(exp.dateFrom);
            const dot = statusDot(exp.status);
            const clickable = !!onExperimentClick;
            const label = exp.productName || exp.description || `#${String(exp.id || '').slice(-4)}`;
            return (
              <Box
                key={exp.id || `${exp.machineId}-${exp.dateFrom}`}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onExperimentClick(exp) : undefined}
                onKeyDown={(e) => {
                  if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onExperimentClick(exp);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 0.75,
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'background 0.15s ease',
                  '&:hover': clickable ? { background: IC.surfaceAlt } : undefined,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: `${IFontSizes.xs}px`,
                    fontWeight: IFontWeights.label,
                    color: IC.tx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={label}
                >
                  {label}
                </Typography>
                {exp.machineId != null && (
                  <Typography
                    sx={{
                      fontSize: `${IFontSizes.xs}px`,
                      color: IC.txM,
                      ...INumericSx,
                      flexShrink: 0,
                    }}
                  >
                    {String(exp.machineId)}
                  </Typography>
                )}
                {days != null && (
                  <Typography
                    sx={{
                      fontSize: `${IFontSizes.xs}px`,
                      color: IC.blu,
                      fontWeight: IFontWeights.label,
                      ...INumericSx,
                      flexShrink: 0,
                    }}
                  >
                    {days}{t('insights_v2_inaction_days_short')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {data && (mt === 'only_accepted' || mt === 'both_pending_and_accepted') && data.daysUntilNext != null && preview.length === 0 && (
        <Box
          role={onCountdownClick ? 'button' : undefined}
          tabIndex={onCountdownClick ? 0 : undefined}
          onClick={onCountdownClick || undefined}
          onKeyDown={(e) => {
            if (onCountdownClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onCountdownClick();
            }
          }}
          aria-label={onCountdownClick ? t('insights_v2_a11y_open_agenda_countdown') : undefined}
          sx={{
            mt: 0.5,
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            background: IC.bluSoft,
            border: `1px solid ${IC.bluSoft}`,
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            cursor: onCountdownClick ? 'pointer' : 'default',
            transition: 'background 0.15s ease',
            '&:hover': onCountdownClick ? { background: 'rgba(51, 122, 183, 0.18)' } : undefined,
          }}
        >
          <Typography
            sx={{
              fontSize: `${IFontSizes.lg}px`,
              fontWeight: IFontWeights.strong,
              color: IC.blu,
              ...INumericSx,
            }}
          >
            {data.daysUntilNext}d
          </Typography>
          <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.blu }}>
            hasta el próximo
          </Typography>
        </Box>
      )}
      {onSeeMore && data && mt !== 'no_experiments' && (
        <>
          <Divider sx={{ mt: 1, borderColor: IC.bdr }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={onSeeMore}
              sx={{
                fontSize: `${IFontSizes.xs}px`,
                fontWeight: IFontWeights.label,
                color: IC.ai,
                textTransform: 'none',
                minWidth: 0,
                px: 0.75,
                '&:hover': { background: IC.aiSoft },
              }}
            >
              {t('insights_v2_actions_see_more')}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default UpcomingCmAgenda;
